import type { Template } from '../ir/types';
import { InstructionError } from '../ir/values';
import { isComment, isElement, isText } from './nodes';

export const HOLE_START = 'c:h';
export const HOLE_END = '/c:h';

const SVG_NS = 'http://www.w3.org/2000/svg';
const MATHML_NS = 'http://www.w3.org/1998/Math/MathML';

/**
 * Content slots that are the only child of their element: the markup before
 * ends an opening tag and the markup after closes it. A fresh copy gives such
 * a slot one text node rather than two markers, so the common `<td>{value}</td>`
 * costs one node and a write; it takes markers only if its value becomes markup.
 */
const soleSlots = new WeakMap<Template, readonly boolean[]>();

export function soleTextSlots(template: Template): readonly boolean[] {
  let sole = soleSlots.get(template);
  if (sole) return sole;
  let html = template.parts[0] ?? '';
  sole = template.slots.map((slot, i) => {
    const before = html;
    const after = template.parts[i + 1] ?? '';
    html += (slot.kind === 'child' ? SOLE_MARK : '') + after;
    if (slot.kind !== 'child' || !before.endsWith('>') || !after.startsWith('</')) return false;
    const open = before.lastIndexOf('<');
    // An opening tag: `<` then a letter, not a closing tag or a comment.
    return open >= 0 && /^<[a-zA-Z]/.test(before.slice(open, open + 2));
  });
  soleSlots.set(template, sole);
  return sole;
}

/** Placeholder text a sole slot parses to; any text would do, and a space survives every parent. */
const SOLE_MARK = ' ';

/** The markup the browser parses: a content slot is a pair of hole markers, or one text node when it is sole. */
function clientHtml(template: Template): string {
  const sole = soleTextSlots(template);
  let html = template.parts[0] ?? '';
  for (let i = 0; i < template.slots.length; i++) {
    const slot = template.slots[i];
    if (slot?.kind === 'child') html += sole[i] ? SOLE_MARK : `<!--${HOLE_START}--><!--${HOLE_END}-->`;
    html += template.parts[i + 1] ?? '';
  }
  return html;
}

/**
 * Parsed templates, per template and namespace. Inside an `<svg>` the same
 * markup parses to different elements, so it is parsed inside a wrapper of
 * that language and the wrapper dropped.
 */
const parsed = new WeakMap<Template, Map<string, Element>>();

function parse(template: Template, ns: string | undefined): Element {
  let byNs = parsed.get(template);
  if (!byNs) {
    byNs = new Map();
    parsed.set(template, byNs);
  }
  const key = ns ?? '';
  let root = byNs.get(key);
  if (!root) {
    const holder = document.createElement('template');
    const html = clientHtml(template);
    if (ns === SVG_NS) holder.innerHTML = `<svg>${html}</svg>`;
    else if (ns === MATHML_NS) holder.innerHTML = `<math>${html}</math>`;
    else holder.innerHTML = html;
    const wrapper = holder.content.firstChild;
    const first = ns === SVG_NS || ns === MATHML_NS ? wrapper?.firstChild : wrapper;
    if (!first || !isElement(first) || first.nextSibling) {
      throw new InstructionError('A template must parse to exactly one element');
    }
    root = first;
    byNs.set(key, root);
  }
  return root;
}

/**
 * A copy of the parsed template in this document, made once: cloning it keeps
 * every row in this document, where importing from the template's inert
 * document adopts each copy on the way in.
 */
const masters = new WeakMap<Element, Element>();

/** Custom elements run their constructors when copied, so a template with one is never kept as a spare copy. */
const hasCustomElement = new WeakMap<Template, boolean>();

/**
 * A fresh copy of the template's element, ready for its slots.
 *
 * Never a clone of the parsed original: that belongs to the template's inert
 * document, so a custom element in it would not upgrade and a check against
 * this window's element classes would fail. A template without custom
 * elements clones a master imported into this document once; one with them is
 * imported every time, so no element's constructor runs for a copy nobody uses.
 */
export function cloneTemplate(template: Template, ns: string | undefined): Element {
  const original = parse(template, ns);
  let custom = hasCustomElement.get(template);
  if (custom === undefined) {
    custom = /<[a-zA-Z][a-zA-Z0-9]*-/.test(template.parts.join(''));
    hasCustomElement.set(template, custom);
  }
  if (custom) return document.importNode(original, true);
  let master = masters.get(original);
  // A test may swap the window; a master from another document would copy into it.
  if (!master || master.ownerDocument !== document) {
    master = document.importNode(original, true);
    masters.set(original, master);
  }
  // SAFETY: a deep copy of an element is an element of the same kind.
  return master.cloneNode(true) as Element;

}

/** The `index`th child as the template counts them: a hole and its content are one. */
function childAt(parent: Node, index: number): Node {
  let node = parent.firstChild;
  for (let at = 0; node; at++) {
    if (at === index) return node;
    if (isComment(node) && node.data === HOLE_START) {
      let depth = 1;
      while (depth > 0) {
        node = node.nextSibling;
        if (!node) break;
        if (isComment(node)) {
          if (node.data === HOLE_START) depth++;
          else if (node.data === HOLE_END) depth--;
        }
      }
    }
    node = node ? node.nextSibling : null;
  }
  throw new InstructionError(`A template slot points past the end of its parent (child ${index})`);
}

/**
 * Each slot's path as plain child positions in a fresh copy, where every hole
 * is exactly its two markers. Worked out once per template on the parsed
 * original, so a mount walks straight to each slot.
 */
const freshPaths = new WeakMap<Template, (readonly number[])[]>();

function rawPaths(template: Template, ns: string | undefined): (readonly number[])[] {
  let paths = freshPaths.get(template);
  if (paths) return paths;
  const original = parse(template, ns);
  paths = template.slots.map((slot) => {
    const raw: number[] = [];
    let node: Node = original;
    for (const step of slot.path) {
      node = childAt(node, step);
      let at = 0;
      for (let sibling = node.previousSibling; sibling; sibling = sibling.previousSibling) at++;
      raw.push(at);
    }
    return raw;
  });
  freshPaths.set(template, paths);
  return paths;
}

/** The slot targets in a copy just made by `cloneTemplate`, found by position alone. */
/**
 * How to reach every slot of a fresh copy, visiting each node on the way
 * once: step `i` finds node `i + 1` from a node already found - its parent's
 * first child, or a sibling found before it - and walks `count` siblings on.
 * `slots` names, for each slot, the node that is its target (0 is the root).
 */
interface WalkPlan {
  readonly from: Int32Array;
  readonly first: Uint8Array;
  readonly count: Int32Array;
  readonly slots: Int32Array;
}

const walkPlans = new WeakMap<Template, WalkPlan>();

function walkPlan(template: Template, ns: string | undefined): WalkPlan {
  const known = walkPlans.get(template);
  if (known) return known;
  const paths = rawPaths(template, ns);
  const from: number[] = [];
  const first: number[] = [];
  const count: number[] = [];
  // A found node, by its parent's index and its position among its siblings.
  const index = new Map<string, number>();
  // The last child found under each parent, to walk on from.
  const lastChild = new Map<number, { readonly at: number; readonly node: number }>();
  const slots = paths.map((path) => {
    let parent = 0;
    for (const at of path) {
      const key = `${parent}/${at}`;
      let node = index.get(key);
      if (node === undefined) {
        const previous = lastChild.get(parent);
        node = from.length + 1;
        if (previous && previous.at <= at) {
          from.push(previous.node);
          first.push(0);
          count.push(at - previous.at);
        } else {
          from.push(parent);
          first.push(1);
          count.push(at);
        }
        index.set(key, node);
        if (!previous || previous.at < at) lastChild.set(parent, { at, node });
      }
      parent = node;
    }
    return parent;
  });
  const plan = { from: Int32Array.from(from), first: Uint8Array.from(first), count: Int32Array.from(count), slots: Int32Array.from(slots) };
  walkPlans.set(template, plan);
  return plan;
}

/**
 * The nodes a walk has found so far. One array for every walk: a walk runs to
 * its end before another starts, and a row is one walk, so a list of a
 * thousand rows allocates it once rather than a thousand times.
 */
const found: (Node | null)[] = [];

export function freshSlotTargets(root: Element, template: Template, ns: string | undefined): Node[] {
  const plan = walkPlan(template, ns);
  const steps = plan.from.length;
  found[0] = root;
  // A step only names a node an earlier step found; the plan is built in that order.
  for (let i = 0; i < steps; i++) {
    const start = found[plan.from[i] ?? 0] ?? null;
    let node: Node | null = start === null ? null : plan.first[i] === 1 ? start.firstChild : start;
    for (let n = plan.count[i] ?? 0; n > 0 && node; n--) node = node.nextSibling;
    found[i + 1] = node;
  }
  const targets: Node[] = [];
  for (let i = 0; i < plan.slots.length; i++) {
    const node = found[plan.slots[i] ?? 0];
    if (!node) throw new InstructionError('A template slot points past the end of its copy');
    targets.push(node);
  }
  // Nothing of this copy is held once its targets are handed over.
  found.fill(null, 0, steps + 1);
  return targets;
}

/**
 * The node each slot names under `root`: the element for an attribute or an
 * event, the opening hole marker for content. Every slot is found before any
 * is filled, so filling one cannot move another.
 */
export function slotTargets(root: Element, template: Template): Node[] {
  const sole = soleTextSlots(template);
  const targets: Node[] = [];
  for (let i = 0; i < template.slots.length; i++) {
    const slot = template.slots[i];
    if (!slot) continue;
    if (sole[i]) {
      // The only child of its element: the server wrote text bare, or nothing
      // for an empty value, or markers when the value was markup.
      let parent: Node = root;
      for (const step of slot.path.slice(0, -1)) parent = childAt(parent, step);
      const first = parent.firstChild;
      if (first && (isText(first) || (isComment(first) && first.data === HOLE_START))) {
        targets.push(first);
      } else {
        const empty = document.createTextNode('');
        parent.insertBefore(empty, first);
        targets.push(empty);
      }
      continue;
    }
    let node: Node = root;
    for (const step of slot.path) node = childAt(node, step);
    targets.push(node);
  }
  return targets;
}

/** The marker closing the hole that `start` opens. */
export function holeEnd(start: Node): Comment {
  let depth = 1;
  for (let node = start.nextSibling; node; node = node.nextSibling) {
    if (!isComment(node)) continue;
    if (node.data === HOLE_START) depth++;
    else if (node.data === HOLE_END && --depth === 0) return node;
  }
  throw new InstructionError('A template hole has no closing marker');
}

/** The namespace a slot's content is created in. */
export function slotNamespace(ns: 'svg' | 'math' | 'html' | undefined, inherited: string | undefined): string | undefined {
  if (ns === undefined) return inherited;
  if (ns === 'svg') return SVG_NS;
  if (ns === 'math') return MATHML_NS;
  return undefined;
}
